<template>
  <div class="p-grid">
    <div class="p-col-12">
      <div class="card p-fluid warnings-settings">
        <h4>Warnings &amp; Approvals</h4>
        <p class="hint">
          Warnings about quote values that look wrong, accounts with no sales exec, and who handles new customer approvals. Admin only.
        </p>

        <div v-if="loading" class="p-text-center"><ProgressSpinner /></div>

        <div v-else-if="loadError" class="p-invalid">{{ loadError }}</div>

        <div v-else>
          <TabView>
            <TabPanel :header="tabHeader('anomaly')" :active.sync="tabActive.anomaly">
              <p class="hint tab-explain">{{ tabExplain('anomaly') }}</p>

              <div class="p-field p-d-flex p-ai-center p-mb-1">
                <InputSwitch id="valueRatioEnabled" v-model="form.valueRatioEnabled" />
                <label for="valueRatioEnabled" class="p-ml-2 p-mb-0">Warn when a value jumps</label>
              </div>
              <div class="p-field p-d-flex p-ai-center">
                <label for="valueRatioY" class="p-mr-2 p-mb-0 inline-label">More than</label>
                <InputNumber id="valueRatioY" v-model="form.valueRatioY" mode="decimal" :minFractionDigits="0"
                  :maxFractionDigits="2" suffix=" times" :disabled="!form.valueRatioEnabled" class="short-input"
                  :class="{ 'p-invalid': errors.valueRatioY }" />
              </div>
              <small class="hint">The value is more than this many times the previous version's, or its value before the edit.</small>
              <small class="p-invalid" v-if="errors.valueRatioY">{{ errors.valueRatioY }}</small>

              <div class="p-field p-d-flex p-ai-center p-mt-3 p-mb-1">
                <InputSwitch id="valueOverEnabled" v-model="form.valueOverEnabled" />
                <label for="valueOverEnabled" class="p-ml-2 p-mb-0">Warn when a value is over a limit</label>
              </div>
              <div class="p-field p-d-flex p-ai-center">
                <label for="valueOverX" class="p-mr-2 p-mb-0 inline-label">Over</label>
                <InputNumber id="valueOverX" v-model="form.valueOverX" mode="currency" currency="GBP" locale="en-GB"
                  :disabled="!form.valueOverEnabled" class="short-input" :class="{ 'p-invalid': errors.valueOverX }" />
              </div>
              <small class="hint">Any quote value above this amount.</small>
              <small class="p-invalid" v-if="errors.valueOverX">{{ errors.valueOverX }}</small>

              <div class="p-field p-d-flex p-ai-center p-mt-3 p-mb-1">
                <InputSwitch id="twoActiveEnabled" v-model="form.twoActiveEnabled" />
                <label for="twoActiveEnabled" class="p-ml-2 p-mb-0">Warn about an enquiry with two Active quotes</label>
              </div>
              <small class="hint">Checked by the nightly scan only.</small>

              <div class="p-field p-d-flex p-ai-center p-mt-3 p-mb-1">
                <InputSwitch id="nightlyEnabled" v-model="form.nightlyEnabled" />
                <label for="nightlyEnabled" class="p-ml-2 p-mb-0">Nightly scan</label>
              </div>
              <small class="hint">At 02:30 the quotes saved since the last scan are checked; new warnings are logged and emailed in one summary.</small>
              <small class="p-invalid" v-if="errors.nightlyEnabled">{{ errors.nightlyEnabled }}</small>

              <div class="p-field p-mt-3">
                <label for="recipientUserIds">Email warnings to</label>
                <MultiSelect id="recipientUserIds" v-model="form.recipientUserIds" :options="userOptions"
                  optionLabel="label" optionValue="id" :filter="true" placeholder="Nobody"
                  :class="{ 'p-invalid': errors.recipientUserIds }" />
                <small class="hint">One email per warning on save, and one summary per night.</small>
                <small class="p-invalid" v-if="errors.recipientUserIds">{{ errors.recipientUserIds }}</small>
              </div>

              <div class="p-d-flex p-ai-center p-flex-wrap">
                <Button id="runScanNow" label="Run scan now" icon="pi pi-play" class="p-button-secondary p-mr-3"
                  style="width: auto" :disabled="scanning" @click="runScan" />
                <span id="lastScanAt" class="hint p-mb-0">Last scan: {{ lastScan || 'never' }}</span>
              </div>
              <small class="hint p-mt-1">Runs the nightly scan now, with the saved settings.</small>
            </TabPanel>

            <TabPanel :header="tabHeader('approval')" :active.sync="tabActive.approval">
              <p class="hint tab-explain">{{ tabExplain('approval') }}</p>

              <div class="p-field">
                <label for="approvalRecipientUserIds">Who gets the approval email</label>
                <MultiSelect id="approvalRecipientUserIds" v-model="form.approvalRecipientUserIds" :options="userOptions"
                  optionLabel="label" optionValue="id" :filter="true" placeholder="Nobody"
                  :class="{ 'p-invalid': errors.approvalRecipientUserIds }" />
                <small class="hint">Emailed when a new customer needs approval. The same switch as "Get customer approval emails" on the user form.</small>
                <small class="p-invalid" v-if="errors.approvalRecipientUserIds">{{ errors.approvalRecipientUserIds }}</small>
              </div>

              <div class="p-field">
                <label>Who can approve</label>
                <p id="adminOnlyNote" class="hint p-mt-0">Only Admin users can approve.</p>
                <div v-if="!form.approvers.length" class="hint">There are no active Admin users.</div>
                <div v-for="a in form.approvers" :key="a.userId" class="p-field-checkbox approver-row">
                  <Checkbox :id="'approver' + a.userId" v-model="a.canApprove" :binary="true" :disabled="a.reset" />
                  <label :for="'approver' + a.userId">{{ a.name }}</label>
                  <small class="approver-note p-ml-2" :id="'approverNote' + a.userId">{{ approverNote(a) }}</small>
                  <Button v-if="!a.fromRole && !a.reset" :id="'resetApprover' + a.userId" label="Reset to role"
                    class="p-button-link p-button-sm p-ml-2 reset-link" @click="resetApprover(a)" />
                </div>
                <small class="hint">"from role": follows the Admin role's Customer Approval permission. "set here": this user's own setting.</small>
                <small class="p-invalid" v-if="errors.approvers">{{ errors.approvers }}</small>
              </div>
            </TabPanel>

            <TabPanel :header="tabHeader('noExec')" :active.sync="tabActive.noExec">
              <p class="hint tab-explain">{{ tabExplain('noExec') }}</p>
              <p v-if="!noExecReady" id="noExecNotReady" class="hint">Not available yet: the server does not have these checks.</p>

              <div class="p-field p-d-flex p-ai-center p-mb-1">
                <InputSwitch id="noExecUnlinkedEnabled" v-model="form.noExecUnlinkedEnabled" :disabled="!noExecReady" />
                <label for="noExecUnlinkedEnabled" class="p-ml-2 p-mb-0">Customer code not linked to a CRM customer for that branch</label>
              </div>
              <small class="hint">Sales in the months set below under a code no CRM customer of that branch has.</small>

              <div class="p-field p-d-flex p-ai-center p-mt-3 p-mb-1">
                <InputSwitch id="noExecMissingEnabled" v-model="form.noExecMissingEnabled" :disabled="!noExecReady" />
                <label for="noExecMissingEnabled" class="p-ml-2 p-mb-0">Linked customer with no sales exec</label>
              </div>
              <small class="hint">The CRM customer has no sales exec, so its sales count under nobody.</small>

              <div class="p-field p-d-flex p-ai-center p-mt-3">
                <label for="noExecSalesWindowMonths" class="p-mr-2 p-mb-0 inline-label">Sales in the last</label>
                <InputNumber id="noExecSalesWindowMonths" v-model="form.noExecSalesWindowMonths" mode="decimal" :maxFractionDigits="0" :min="1" :max="60"
                  suffix=" months" :disabled="!noExecReady" class="short-input"
                  :class="{ 'p-invalid': errors.noExecSalesWindowMonths }" />
              </div>
              <small class="hint">Only accounts with sales in this many months are listed.</small>
              <small class="p-invalid" v-if="errors.noExecSalesWindowMonths">{{ errors.noExecSalesWindowMonths }}</small>
            </TabPanel>
          </TabView>

          <div class="p-d-flex p-jc-end p-mt-3">
            <Button label="Reset" icon="pi pi-refresh" class="p-button-text p-mr-2" style="width: auto" @click="reset" />
            <Button id="saveWarningSettings" label="Save" icon="pi pi-check" style="width: auto"
              :disabled="saving" @click="save" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import WarningsService, { errorMessage, fieldErrors } from '@/service/WarningsService'
import {
  toForm, toPayload, validate, approverNote, formatDateTime, scanSummary, tabWithError, noExecSupported, SETTINGS_TABS
} from './warningsSettingsForm'

function tabsActive(key) {
  return SETTINGS_TABS.reduce((out, t) => { out[t.key] = t.key === key; return out }, {})
}

export default {
  data: () => ({
    warningsService: new WarningsService(),
    saved: null,
    form: toForm(null),
    errors: {},
    loading: true,
    loadError: '',
    saving: false,
    scanning: false,
    tabActive: tabsActive('anomaly')
  }),
  computed: {
    userOptions() {
      return ((this.saved && this.saved.users) || []).map(u => ({
        id: u.id,
        label: u.roleName ? u.name + ' (' + u.roleName + ')' : u.name
      }))
    },
    noExecReady() {
      return noExecSupported(this.saved)
    },
    lastScan() {
      return formatDateTime(this.saved && (this.saved.lastScanAtIso || this.saved.lastScanAt))
    }
  },
  created() {
    this.load()
  },
  methods: {
    approverNote,
    tabHeader(key) {
      var t = SETTINGS_TABS.find(x => x.key === key)
      var keys = Object.keys(this.errors || {})
      return t.header + (t.fields.some(f => keys.indexOf(f) > -1) ? ' (!)' : '')
    },
    tabExplain(key) {
      return SETTINGS_TABS.find(x => x.key === key).explain
    },
    showTab(key) {
      this.tabActive = tabsActive(key)
    },
    applySaved(data) {
      this.saved = data
      this.form = toForm(data)
    },
    load() {
      this.loading = true
      return this.warningsService.getSettings()
        .then(data => {
          this.applySaved(data)
          this.loadError = ''
        })
        .catch(err => {
          this.loadError = errorMessage(err, 'The warning settings could not be loaded.')
          this.$toast.add({ severity: 'error', summary: 'Not loaded', detail: this.loadError, life: 5000 })
        })
        .finally(() => { this.loading = false })
    },
    resetApprover(a) {
      a.reset = true
    },
    reset() {
      this.errors = {}
      this.form = toForm(this.saved)
    },
    save() {
      this.errors = validate(this.form)
      if (Object.keys(this.errors).length) {
        this.showTab(tabWithError(this.errors))
        this.$toast.add({ severity: 'error', summary: 'Not saved', detail: 'Please correct the values marked.', life: 5000 })
        return Promise.resolve()
      }
      this.saving = true
      return this.warningsService.saveSettings(toPayload(this.form, this.saved))
        .then(data => {
          this.applySaved(data)
          this.$toast.add({
            severity: 'success',
            summary: 'Settings saved',
            detail: 'The warning and approval settings were saved.',
            life: 5000
          })
        })
        .catch(err => {
          // a 400 may carry field messages (shown inline); a 409 carries only the top-level error
          this.errors = fieldErrors(err)
          var withError = tabWithError(this.errors)
          if (withError) this.showTab(withError)
          this.$toast.add({
            severity: 'error',
            summary: 'Not saved',
            detail: errorMessage(err, 'The warning settings could not be saved.'),
            life: 5000
          })
        })
        .finally(() => { this.saving = false })
    },
    runScan() {
      this.scanning = true
      return this.warningsService.runScan()
        .then(result => {
          this.$toast.add({ severity: 'success', summary: 'Scan finished', detail: scanSummary(result), life: 8000 })
          // only the last scan time is taken over: unsaved edits on the form stay
          return this.warningsService.getSettings()
            .then(data => { this.saved = data })
            .catch(() => {})
        })
        .catch(err => {
          this.$toast.add({
            severity: 'error',
            summary: 'Not run',
            detail: errorMessage(err, 'The scan could not be run.'),
            life: 5000
          })
        })
        .finally(() => { this.scanning = false })
    }
  }
}
</script>

<style scoped>
/* the card is full width like every other page; only the form fields keep a readable width */
.warnings-settings .p-field, .warnings-settings .p-formgrid, .warnings-settings .p-field-checkbox { max-width: 760px; }
.warnings-settings .tab-explain { margin-top: 0; }
.warnings-settings .hint { display: block; color: #6c757d; margin-bottom: 0.5rem; }
.warnings-settings span.hint { display: inline; }
.warnings-settings small.p-invalid { display: block; color: #d32f2f; }
.warnings-settings .inline-label { white-space: nowrap; min-width: 5rem; }
.warnings-settings .short-input { max-width: 14rem; }
.warnings-settings .approver-row { margin-bottom: 0.5rem; }
.warnings-settings .approver-note { color: #6c757d; font-style: italic; }
.warnings-settings .reset-link { width: auto; padding: 0 0.25rem; }
</style>
